import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { HitApi } from '../../../Api/ApiHit';
import { sendNotificationApi, sendBulkNotificationApi, searchClient } from '../../../constant/Constant';
import { Send, ArrowLeft, Users, User, Search, X, Bell } from 'lucide-react';
import toast from 'react-hot-toast';

const SendNotification = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState('all'); // 'all', 'single', 'bulk'
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [type, setType] = useState('general');
  const [sending, setSending] = useState(false);

  // Client search for single/bulk
  const [clientSearch, setClientSearch] = useState('');
  const [clientResults, setClientResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedClients, setSelectedClients] = useState([]);

  const searchClients = () => {
    if (!clientSearch.trim()) return;
    setSearching(true);
    HitApi({
      page: 1,
      limit: 20,
      search: {
        $or: [
          { name: { $regex: clientSearch, $options: 'i' } },
          { phoneNumber: { $regex: clientSearch, $options: 'i' } },
        ],
      },
    }, searchClient)
      .then((res) => {
        const docs = res?.data?.docs || res?.data || [];
        setClientResults(Array.isArray(docs) ? docs : []);
      })
      .catch(() => toast.error('Failed to search clients'))
      .finally(() => setSearching(false));
  };

  const addClient = (client) => {
    if (selectedClients.find((c) => c._id === client._id)) return;
    setSelectedClients((prev) => [...prev, client]);
    setClientResults([]);
    setClientSearch('');
  };

  const removeClient = (clientId) => {
    setSelectedClients((prev) => prev.filter((c) => c._id !== clientId));
  };

  const handleSend = () => {
    if (!title.trim() || !body.trim()) {
      toast.error('Title and body are required');
      return;
    }

    if (mode === 'single' && selectedClients.length !== 1) {
      toast.error('Please select exactly one client');
      return;
    }

    if (mode === 'bulk' && selectedClients.length === 0) {
      toast.error('Please select at least one client');
      return;
    }

    setSending(true);

    if (mode === 'single') {
      HitApi({
        clientId: selectedClients[0]._id,
        title,
        body,
        type,
        data: { type },
      }, sendNotificationApi)
        .then((res) => {
          if (res?.statusCode === 200) {
            toast.success(`Notification sent to ${selectedClients[0].name}`);
            navigate('/app-control/notifications');
          } else {
            toast.error(res?.message || 'Failed to send notification');
          }
        })
        .catch(() => toast.error('Failed to send notification'))
        .finally(() => setSending(false));
    } else {
      const payload = { title, body, type, data: { type } };
      if (mode === 'bulk') {
        payload.clientIds = selectedClients.map((c) => c._id);
      }
      // mode === 'all' sends without clientIds (broadcasts to all)

      HitApi(payload, sendBulkNotificationApi)
        .then((res) => {
          if (res?.statusCode === 200) {
            toast.success(res?.message || 'Notification sent successfully');
            navigate('/app-control/notifications');
          } else {
            toast.error(res?.message || 'Failed to send notification');
          }
        })
        .catch(() => toast.error('Failed to send notification'))
        .finally(() => setSending(false));
    }
  };

  return (
    <div className="p-6 max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate('/app-control/notifications')}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Send Notification</h1>
          <p className="text-sm text-gray-500 mt-0.5">Compose and send push notifications</p>
        </div>
      </div>

      <div className="space-y-6">
        {/* Target Mode */}
        <div className="bg-white rounded-lg shadow border border-gray-100 p-5">
          <label className="block text-sm font-semibold text-gray-700 mb-3">Send To</label>
          <div className="grid grid-cols-3 gap-3">
            {[
              { key: 'all', label: 'All Users', icon: Users, desc: 'Broadcast to everyone' },
              { key: 'single', label: 'Single User', icon: User, desc: 'Send to one client' },
              { key: 'bulk', label: 'Selected Users', icon: Users, desc: 'Pick multiple clients' },
            ].map((opt) => (
              <button
                key={opt.key}
                onClick={() => { setMode(opt.key); setSelectedClients([]); }}
                className={`p-4 rounded-lg border-2 text-left transition-all ${
                  mode === opt.key
                    ? 'border-black bg-gray-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <opt.icon className={`w-5 h-5 mb-2 ${mode === opt.key ? 'text-black' : 'text-gray-400'}`} />
                <p className="text-sm font-semibold text-gray-900">{opt.label}</p>
                <p className="text-xs text-gray-500 mt-0.5">{opt.desc}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Client Search (for single/bulk mode) */}
        {(mode === 'single' || mode === 'bulk') && (
          <div className="bg-white rounded-lg shadow border border-gray-100 p-5">
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Select Client{mode === 'bulk' ? 's' : ''}
            </label>

            {/* Selected clients */}
            {selectedClients.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {selectedClients.map((client) => (
                  <div
                    key={client._id}
                    className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-full text-sm"
                  >
                    <span className="font-medium">{client.name}</span>
                    <span className="text-gray-400">{client.phoneNumber}</span>
                    <button onClick={() => removeClient(client._id)} className="text-gray-400 hover:text-red-500">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Search input */}
            {(mode === 'bulk' || selectedClients.length === 0) && (
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name or phone..."
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && searchClients()}
                  className="w-full pl-9 pr-20 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black/10"
                />
                <button
                  onClick={searchClients}
                  disabled={searching}
                  className="absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1 bg-black text-white text-xs rounded-md hover:bg-gray-800 disabled:opacity-50"
                >
                  {searching ? '...' : 'Search'}
                </button>
              </div>
            )}

            {/* Results dropdown */}
            {clientResults.length > 0 && (
              <div className="mt-2 border border-gray-200 rounded-lg max-h-48 overflow-y-auto">
                {clientResults.map((client) => (
                  <button
                    key={client._id}
                    onClick={() => addClient(client)}
                    className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 text-left border-b border-gray-100 last:border-0"
                  >
                    <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center text-xs font-bold text-gray-600">
                      {client.name?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{client.name}</p>
                      <p className="text-xs text-gray-500">{client.phoneNumber}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Notification Content */}
        <div className="bg-white rounded-lg shadow border border-gray-100 p-5">
          <label className="block text-sm font-semibold text-gray-700 mb-3">Notification Content</label>

          <div className="space-y-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Type</label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black/10"
              >
                <option value="general">General</option>
                <option value="promotion">Promotion</option>
                <option value="booking">Booking</option>
                <option value="payment">Payment</option>
                <option value="reminder">Reminder</option>
              </select>
            </div>

            <div>
              <label className="block text-xs text-gray-500 mb-1">Title</label>
              <input
                type="text"
                placeholder="Notification title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black/10"
                maxLength={100}
              />
              <p className="text-xs text-gray-400 mt-1 text-right">{title.length}/100</p>
            </div>

            <div>
              <label className="block text-xs text-gray-500 mb-1">Message</label>
              <textarea
                placeholder="Notification message body..."
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={4}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black/10 resize-none"
                maxLength={500}
              />
              <p className="text-xs text-gray-400 mt-1 text-right">{body.length}/500</p>
            </div>
          </div>
        </div>

        {/* Preview */}
        {(title || body) && (
          <div className="bg-white rounded-lg shadow border border-gray-100 p-5">
            <label className="block text-sm font-semibold text-gray-700 mb-3">Preview</label>
            <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-black rounded-lg shrink-0">
                  <Bell className="w-4 h-4 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900">{title || 'Notification Title'}</p>
                  <p className="text-xs text-gray-600 mt-0.5">{body || 'Message body will appear here...'}</p>
                  <p className="text-[10px] text-gray-400 mt-1">just now</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Send Button */}
        <div className="flex items-center gap-3 justify-end">
          <button
            onClick={() => navigate('/app-control/notifications')}
            className="px-6 py-2.5 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={sending || !title.trim() || !body.trim()}
            className="flex items-center gap-2 px-6 py-2.5 bg-black text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {sending ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              <Send className="w-4 h-4" />
            )}
            {sending ? 'Sending...' : `Send to ${mode === 'all' ? 'All Users' : mode === 'single' ? 'Client' : `${selectedClients.length} Client(s)`}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SendNotification;
